"use client";

import React, { createContext, useCallback, useEffect, useState } from "react";
import {
  DEFAULT_THEME,
  getPreset,
  getFontFamily,
  type ThemeSettings,
  type ThemeColors,
} from "./presets";
import { api } from "@/lib/api";
import {
  AA_NORMAL,
  DEEPEN_PERCENTS,
  MIN_HOVER_DELTA,
  TINT_BACKDROP,
  inkVariant,
  isTriplet,
  mixTriplets,
  relativeLuminance,
} from "./scale";

/* ── helpers ── */

/** WCAG contrast ratio between two luminance values. */
function contrastRatio(a: number, b: number): number {
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
}

const FOREGROUND_LIGHT = { triplet: "255 255 255", hex: "#ffffff" };
const FOREGROUND_DARK = { triplet: "15 23 42", hex: "#0f172a" };

/**
 * Pick white or near-black text for a given background colour, whichever
 * yields the higher contrast ratio. Falls back to white when the triplet
 * cannot be parsed, matching the previous hard-coded behaviour.
 */
export function readableForeground(triplet: string): { triplet: string; hex: string } {
  if (!isTriplet(triplet)) return FOREGROUND_LIGHT;
  const background = relativeLuminance(triplet);
  const onLight = contrastRatio(background, relativeLuminance(FOREGROUND_LIGHT.triplet));
  const onDark = contrastRatio(background, relativeLuminance(FOREGROUND_DARK.triplet));
  return onDark > onLight ? FOREGROUND_DARK : FOREGROUND_LIGHT;
}

/**
 * The tint percentages, the AA threshold and the hover step all live in
 * ./scale so that tailwind.config.ts, this file and the Settings preview cannot
 * drift apart. Only the parts that need the DOM stay here.
 */

/**
 * Derive a high-contrast variant of a state colour for use as TEXT on a surface
 * tinted with that same colour (the `bg-danger-50` + `text-danger-700` pattern).
 *
 * The raw state colour is chosen to look right as a solid fill, so it is often
 * too close to the surface to read as text — `--color-warning` on white is a
 * typical failure. This walks the colour away from the surface (towards black
 * on a light surface, towards white on a dark one) until it clears AA against
 * BOTH the plain surface and the 100-tint of itself, so the same class works in
 * both modes without a second stored value.
 *
 * Returns the input unchanged when it already passes, and never overshoots past
 * 90% blend so the hue stays recognisable.
 */
export function strongVariant(base: string, surface: string): string {
  if (!/^\d+\s+\d+\s+\d+$/.test(base.trim())) return base;
  if (!/^\d+\s+\d+\s+\d+$/.test(surface.trim())) return base;

  const surfaceLuminance = relativeLuminance(surface);
  // A light surface needs a darker colour, a dark surface needs a lighter one.
  const target = surfaceLuminance > 0.5 ? "0 0 0" : "255 255 255";
  // Worst-case backdrop this colour will ever be read on.
  const backdropLuminance = relativeLuminance(mixTriplets(surface, base, TINT_BACKDROP));

  let candidate = base;
  for (let weight = 0; weight <= 0.9; weight += 0.05) {
    candidate = mixTriplets(base, target, weight);
    const luminance = relativeLuminance(candidate);
    if (
      contrastRatio(luminance, surfaceLuminance) >= AA_NORMAL &&
      contrastRatio(luminance, backdropLuminance) >= AA_NORMAL
    ) {
      return candidate;
    }
  }
  return candidate;
}

/** State colours that get an auto-derived `-strong` companion token. */
const STRONG_KEYS = ["danger", "success", "warning", "info", "primary", "secondary"] as const;

/** Smallest sRGB channel shift a person reliably notices on a large filled area. */
/** Must match the `800` step in `semanticScale()` in tailwind.config.ts. */
const HOVER_MIX = DEEPEN_PERCENTS[800] / 100;

function maxChannelDelta(a: string, b: string): number {
  const first = a.trim().split(/\s+/).map(Number);
  const second = b.trim().split(/\s+/).map(Number);
  return Math.max(...first.map((channel, i) => Math.abs(channel - second[i])));
}

/**
 * Choose which end of the range shades 800–950 should travel towards.
 *
 * Hover normally wants to move FURTHER from the surface — darker on a light
 * page, lighter on a dark one. But a button can already be pinned against that
 * end: in the olive-green preset the primary colour is nearly as dark as the
 * body text, so pushing it darker moves it about two sRGB units and the button
 * feels dead under the cursor.
 *
 * CSS `color-mix()` cannot make that decision because it cannot branch on
 * luminance, so the theme decides here and publishes the answer as a variable.
 * The config then just interpolates towards whatever pole it is handed, which
 * keeps the whole scale declarative and still leaves Settings in control.
 */
export function deepPole(strong: string, surface: string): string {
  const BLACK = "0 0 0";
  const WHITE = "255 255 255";
  const away = relativeLuminance(strong) < relativeLuminance(surface) ? BLACK : WHITE;
  if (maxChannelDelta(strong, mixTriplets(strong, away, HOVER_MIX)) >= MIN_HOVER_DELTA) return away;
  // No room left in that direction, so the hover has to move the other way.
  return away === BLACK ? WHITE : BLACK;
}

/**
 * Build the modal / video-wall scrim for a given page background.
 *
 * A scrim only works if it is DARKER than whatever it covers. An absolute
 * "make it dark" rule is not enough: a near-black overlay is still lighter than
 * a dark preset's `--color-bg`, so the backdrop would glow when a modal opens —
 * which is exactly the bug `bg-th-overlay/40` has today.
 *
 * So the target is relative: darken the background's own colour (keeping its
 * hue) until it is comfortably below the background's luminance, and never
 * above an absolute ceiling. The result is used as `bg-th-overlay/40`.
 */
export function scrimColor(bg: string): string {
  if (!/^\d+\s+\d+\s+\d+$/.test(bg.trim())) return "15 23 42";

  const bgLuminance = relativeLuminance(bg);
  const ceiling = Math.min(SCRIM_MAX_LUMINANCE, bgLuminance * SCRIM_DARKEN_RATIO);

  let candidate = bg;
  for (let weight = 0; weight <= 1; weight += 0.02) {
    candidate = mixTriplets(bg, "0 0 0", weight);
    if (relativeLuminance(candidate) <= ceiling) return candidate;
  }
  return "0 0 0";
}

/** Absolute darkness ceiling for the scrim, used on light backgrounds. */
const SCRIM_MAX_LUMINANCE = 0.06;
/** How much darker than the background the scrim must be on dark backgrounds. */
const SCRIM_DARKEN_RATIO = 0.35;

function applyThemeToDOM(settings: ThemeSettings, mode: "light" | "dark") {
  const preset = getPreset(settings.preset);
  const base: ThemeColors = mode === "dark" ? preset.dark : preset.light;

  // merge any custom color overrides
  const merged = { ...base, ...settings.colors } as ThemeColors;

  const root = document.documentElement;
  // helpers: convert between hex and "R G B" triplet
  function hexToTriplet(hex: string | null): string | null {
    if (!hex) return null;
    const h = hex.replace('#', '').trim();
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
    const r = parseInt(h.slice(0,2), 16);
    const g = parseInt(h.slice(2,4), 16);
    const b = parseInt(h.slice(4,6), 16);
    return `${r} ${g} ${b}`;
  }
  function tripletToHex(trip: string): string | null {
    const parts = trip.trim().split(/\s+/);
    if (parts.length !== 3) return null;
    const [r,g,b] = parts.map((p) => parseInt(p, 10));
    if ([r,g,b].some((n) => Number.isNaN(n) || n < 0 || n > 255)) return null;
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  const resolvedTriplets: Record<string, string> = {};

  for (const [key, rawValue] of Object.entries(merged)) {
    let triplet: string | null = null;
    let hex: string | null = null;
    const v = (rawValue ?? "").toString().trim();
    if (/^#?[0-9a-fA-F]{6}$/.test(v)) {
      // hex provided
      hex = v.startsWith('#') ? v : `#${v}`;
      triplet = hexToTriplet(hex);
    } else if (/^\d+\s+\d+\s+\d+$/.test(v)) {
      // triplet provided
      triplet = v;
      hex = tripletToHex(v);
    } else {
      // fallback: try to parse rgb(...) strings
      const m = v.match(/rgb\((\d+)\s*,?\s*(\d+)\s*,?\s*(\d+)\)/i);
      if (m) {
        triplet = `${m[1]} ${m[2]} ${m[3]}`;
        hex = tripletToHex(triplet);
      }
    }

    if (triplet) {
      root.style.setProperty(`--color-${key}`, triplet);
      resolvedTriplets[key] = triplet;
    }
    if (hex) root.style.setProperty(`--${key}`, hex);

    // Derive a readable foreground for the primary colour so that text placed on
    // `bg-th-primary` keeps sufficient contrast for light presets as well.
    if (key === "primary" && triplet) {
      const fg = readableForeground(triplet);
      root.style.setProperty("--color-primary-foreground", fg.triplet);
      root.style.setProperty("--primary-foreground", fg.hex);
    }
  }

  // Derived companion tokens. These back the semantic colour scales declared in
  // tailwind.config.ts, so a state colour keeps enough contrast in BOTH modes
  // without storing a second colour per preset.
  //
  // There are two derivations because there are two jobs, with two different
  // WCAG thresholds, and conflating them is what left 19 of 60 preset/mode
  // combinations sitting between 3:1 and 4.5:1 — readable enough to pass a
  // glance, not readable enough to pass a measurement:
  //
  //   -strong  a FILL (3:1). You put `-foreground` text on top of it.
  //   -ink     the TEXT itself (4.5:1). Nothing goes on top of it.
  const surfaceTriplet = resolvedTriplets.surface;
  if (surfaceTriplet) {
    for (const key of STRONG_KEYS) {
      const base = resolvedTriplets[key];
      if (!base) continue;
      const strong = strongVariant(base, surfaceTriplet);
      root.style.setProperty(`--color-${key}-strong`, strong);
      // Text-strength variant, consumed by the `textColor` scale in
      // tailwind.config.ts. It is derived from `base` rather than from `strong`
      // so it stays as close to the theme's chosen colour as readability allows,
      // and it is a no-op whenever the colour already clears 4.5:1 — which is
      // every state colour in dark mode.
      root.style.setProperty(`--color-${key}-ink`, inkVariant(base, surfaceTriplet));
      // Label colour for a SOLID fill of this state colour. It is derived from
      // `strong`, not from `base`, because some preset colours (sky-600 in
      // smart-blue, for example) sit in a dead zone where neither white nor
      // near-black reaches 4.5:1. Solid interactive surfaces therefore use
      // `bg-{key}-700` + `text-{key}-foreground`, which is always AA-safe.
      root.style.setProperty(`--color-${key}-foreground`, readableForeground(strong).triplet);
      // Which way shades 800–950 travel for hover and active states. Publishing
      // the pole instead of the finished colour lets the config interpolate all
      // three depths from one variable.
      root.style.setProperty(`--color-${key}-deep-pole`, deepPole(strong, surfaceTriplet));
    }
  }

  // Modal / video-wall scrim. Derived from the page background so it keeps the
  // theme's hue, and always darker than it so opening a modal dims the page in
  // BOTH schemes. Consumers use `bg-th-overlay/40`, `/80`, etc.
  const bgTriplet = resolvedTriplets.bg ?? resolvedTriplets.surface;
  if (bgTriplet) {
    root.style.setProperty("--color-overlay", scrimColor(bgTriplet));
  }

  // fonts
  root.style.setProperty("--font-heading", getFontFamily(settings.fonts.heading));
  root.style.setProperty("--font-body", getFontFamily(settings.fonts.body));

  // layout tokens
  root.style.setProperty("--card-radius", radiusValue(settings.layout.cardRadius));
  root.style.setProperty("--density", densityValue(settings.layout.density));
  root.style.setProperty("--btn-radius", buttonRadiusValue(settings.ui?.buttonStyle || "rounded"));
  root.style.setProperty("--btn-py", buttonPyValue(settings.ui?.buttonSize || "md"));
  root.style.setProperty("--btn-px", buttonPxValue(settings.ui?.buttonSize || "md"));
  root.style.setProperty("--btn-fs", buttonFontSizeValue(settings.ui?.buttonSize || "md"));
  root.style.setProperty("--icon-size", iconSizeValue(settings.ui?.iconSize || "md"));
  root.style.setProperty("--icon-radius", iconRadiusValue(settings.ui?.iconStyle || "rounded"));

  // custom CSS
  let styleEl = document.getElementById("mc-custom-css");
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = "mc-custom-css";
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = settings.custom_css || "";

  // global UI identity styles for shared Button component and icon wrappers
  let uiStyleEl = document.getElementById("mc-theme-ui");
  if (!uiStyleEl) {
    uiStyleEl = document.createElement("style");
    uiStyleEl.id = "mc-theme-ui";
    document.head.appendChild(uiStyleEl);
  }
  uiStyleEl.textContent = `
    button[data-variant] {
      border-radius: var(--btn-radius) !important;
      font-size: var(--btn-fs) !important;
      padding-inline: var(--btn-px) !important;
      padding-block: var(--btn-py) !important;
    }
    button[data-variant][data-size="sm"],
    button[data-variant][data-size="md"],
    button[data-variant][data-size="lg"] {
      height: auto !important;
      min-height: calc(var(--btn-py) * 2 + 1.5em) !important;
    }
    button[data-variant][data-size="icon"] {
      width: var(--icon-size) !important;
      height: var(--icon-size) !important;
      border-radius: var(--icon-radius) !important;
      padding: 0 !important;
    }
    .icon-surface {
      width: var(--icon-size);
      height: var(--icon-size);
      border-radius: var(--icon-radius);
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
  `;
}

function radiusValue(r: string): string {
  switch (r) {
    case "none": return "0";
    case "sm": return "0.375rem";
    case "md": return "0.5rem";
    case "lg": return "0.75rem";
    case "xl": return "1rem";
    case "2xl": return "1.5rem";
    default: return "1rem";
  }
}

function densityValue(d: string): string {
  switch (d) {
    case "compact": return "0.25rem";
    case "normal": return "0.5rem";
    case "comfortable": return "0.75rem";
    default: return "0.5rem";
  }
}

function buttonRadiusValue(style: string): string {
  switch (style) {
    case "pill":
      return "9999px";
    case "square":
      return "0.25rem";
    case "rounded":
    default:
      return "0.65rem";
  }
}

function buttonPyValue(size: string): string {
  switch (size) {
    case "sm":
      return "0.35rem";
    case "lg":
      return "0.7rem";
    case "md":
    default:
      return "0.5rem";
  }
}

function buttonPxValue(size: string): string {
  switch (size) {
    case "sm":
      return "0.75rem";
    case "lg":
      return "1.25rem";
    case "md":
    default:
      return "1rem";
  }
}

function buttonFontSizeValue(size: string): string {
  switch (size) {
    case "sm":
      return "0.8rem";
    case "lg":
      return "1rem";
    case "md":
    default:
      return "0.9rem";
  }
}

function iconSizeValue(size: string): string {
  switch (size) {
    case "sm":
      return "1.75rem";
    case "lg":
      return "2.5rem";
    case "md":
    default:
      return "2.125rem";
  }
}

function iconRadiusValue(style: string): string {
  switch (style) {
    case "square":
      return "0.35rem";
    case "rounded":
    default:
      return "9999px";
  }
}

const LS_KEY = "mc-theme-cache";

function loadCachedTheme(): ThemeSettings {
  if (typeof window === "undefined") return DEFAULT_THEME;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return DEFAULT_THEME;
}

function cacheTheme(s: ThemeSettings) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(s));
  } catch { /* ignore */ }
}

/* ── Context ── */
interface ThemeContextValue {
  settings: ThemeSettings;
  setSettings: (s: ThemeSettings) => void;
  applyPreview: (s: ThemeSettings) => void;
  saving: boolean;
  save: (s: ThemeSettings) => Promise<void>;
  loading: boolean;
}

export const ThemeContext = createContext<ThemeContextValue>({
  settings: DEFAULT_THEME,
  setSettings: () => {},
  applyPreview: () => {},
  saving: false,
  save: async () => {},
  loading: true,
});

/* ── Provider ── */
export function McThemeProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettingsState] = useState<ThemeSettings>(loadCachedTheme);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // detect current color mode
  const getMode = useCallback((): "light" | "dark" => {
    if (typeof document === "undefined") return "light";
    return document.documentElement.classList.contains("dark") ? "dark" : "light";
  }, []);

  // apply to DOM
  const applyPreview = useCallback(
    (s: ThemeSettings) => {
      applyThemeToDOM(s, getMode());
    },
    [getMode]
  );

  // set + apply + cache
  const setSettings = useCallback(
    (s: ThemeSettings) => {
      setSettingsState(s);
      cacheTheme(s);
      applyPreview(s);
    },
    [applyPreview]
  );

  // save to backend
  const save = useCallback(
    async (s: ThemeSettings) => {
      setSaving(true);
      try {
        await api.put("/admin/theme", s);
        setSettings(s);
      } finally {
        setSaving(false);
      }
    },
    [setSettings]
  );

  // fetch from backend on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<ThemeSettings>("/admin/theme");
        if (!cancelled) setSettings(res.data);
      } catch {
        // use cached/default — no problem
        applyPreview(settings);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // re-apply when dark/light toggles
  useEffect(() => {
    const observer = new MutationObserver(() => {
      applyPreview(settings);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, [settings, applyPreview]);

  return (
    <ThemeContext.Provider value={{ settings, setSettings, applyPreview, saving, save, loading }}>
      {children}
    </ThemeContext.Provider>
  );
}
